import dynamic from "next/dynamic";
import PropTypes from "prop-types";
import { useContext, useState, useEffect } from "react";
import styled from "styled-components";
import withSizes from "react-sizes";
import env from "../config/environment";
import orderApi from "../api/order";
import { PropsContext } from "../context/ItemPropsContext";
import { tnsBlue, white, nsmWhite } from "../styles/colors";
import { flex } from "../styles/flex";
import { WideComponent } from "../styles/responsive";
import { ErrorText } from "../styles/typography";
import { Spin } from "../styles/antd";
import ga4Analytics from "../utils/analytics/ga4Analytics";
import "react-responsive-carousel/lib/styles/carousel.min.css"; // requires a loader

const { ACTIVE_SITE } = env;
const isNightSkyMaps = ACTIVE_SITE === "nightskymaps";
const isOrrery = ACTIVE_SITE === "theplanetsaligned";

const DesignPanel: any = dynamic(() => import("../components/design/DesignPanel"), {
  ssr: false,
});

const DesignPanelOrrery: any = dynamic(() => import("../components/design/DesignPanelOrrery"), {
  ssr: false,
});

const Layout = dynamic(() => import("../components/Layout"), {
  ssr: false,
});

const PreviewPanelPrint = dynamic(() => import("../components/mappanels/PreviewPanelPrint"), {
  ssr: false,
});

const MapPanelOrrery = dynamic(() => import("../components/mappanels/MapPanelOrrery"), {
  ssr: false,
});

const Container = styled.div({
  ...flex.flex,
  ...flex.col,
  minHeight: `100vh`,
  fontFamily: `Lato, Helvetica Neue,Helvetica,Arial,sans-serif`,
});

const InnerContainer = styled.div({
  ...flex.flex,
  width: `100%`,
  height: `100%`,
});

const CenteredContainer = styled.div({
  ...flex.flex,
  ...flex.centerXY,
  height: `100vh`,
});

const WideContainer = styled(WideComponent)({
  flex: 1,
});

const LoadingContainer = styled.div({
  flex: 1,
  ...flex.centerXY,
  height: `100vh`,
  display: "flex",
});

const LoadingContent = styled.div({
  color: "white",
});

const Design = ({ pageProps, windowWidth, windowHeight, isMobile }) => {
  const { setToCartItem, configError, configLoaded, customProps } = useContext(PropsContext);
  const [showMap, setShowMap] = useState(false);
  const [hasLocation] = useState((customProps && customProps.isLocationSelected) || false);

  const renderStarmap = () => {
    if (!showMap) {
      setShowMap(true);
    }
  };

  useEffect(() => {
    if (configLoaded === true) {
      if (pageProps && pageProps.item) {
        const { design, frameColor, isCanvas, isFramed, isDigital, isPendant } = pageProps.item;
        setToCartItem({ customProps: design.customProps, previewImageUrl: null, frameColor, isCanvas, isFramed, isDigital, isPendant });
      }

      // if (pageProps.code) {
      //   const { sizeId, orientation, isFramed, isCanvas, isDigital } = customProps;
      //   let format = "print";
      //   if (isCanvas) format = "canvas";
      //   if (isFramed) format = "frame";
      //   if (isDigital) format = "digital";

      //   let currency = "";
      //   let productPriceCents = 0;
      //   if (priceList && priceList.pricing[orientation][sizeId]) {
      //     currency = priceList.pricing[orientation][sizeId].print.customer.currency;
      //     productPriceCents = priceList.pricing[orientation][sizeId][format].customer.gross;
      //   }
      //   //
      //   addCartItem(
      //     customProps,
      //     currentDesignCartItemId || "",
      //     previewImageUrl,
      //     isCanvas || null,
      //     isFramed || null,
      //     isDigital || isDigital || null,
      //     false, // isPendant
      //     null,
      //     customProps.frameColor || null,
      //     shipping,
      //     orderId,
      //     customerId,
      //     isOrderPaid,
      //     currency,
      //     productPriceCents,
      //     ({ updatedCart, newItem }) => {
      //       console.log(`>>>>> updatedCart: ${JSON.stringify(updatedCart, null, 4)}`);
      //     },
      //   );
      //   //
      //   if (cartingOut) {
      //     analytics.logEvent("Design", "User went to cart");
      //     setCanvasImageUrl(null);
      //   }
      // }
    }
  }, [pageProps, configLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    ga4Analytics.logPageViewCreate();
  }, []);

  const render = () => {
    // TODO: looks wonky if things load too fast. Should use React Lazy.
    if (!configLoaded) {
      return (
        <CenteredContainer>
          <Spin />
        </CenteredContainer>
      );
    }
    const { customerId, orderId, item } = pageProps || { customerId: null, orderId: null, item: null };

    // TODO: looks wonky if things load too fast. Should use React Lazy.
    if (configError) {
      return (
        <CenteredContainer>
          <ErrorText>{configError}</ErrorText>
        </CenteredContainer>
      );
    }

    if (isOrrery) {
      return (
        <Container>
          <InnerContainer onMouseMove={renderStarmap}>
            {!configLoaded ? (
              <LoadingContainer>
                <LoadingContent>LOADING ...</LoadingContent>
              </LoadingContainer>
            ) : (
              <>
                <DesignPanelOrrery loading={!configLoaded} customerId={customerId} orderId={orderId} item={item} />
                <WideContainer>
                  <MapPanelOrrery />
                </WideContainer>
              </>
            )}
          </InnerContainer>
        </Container>
      );
    }

    return (
      <Container>
        <InnerContainer onMouseMove={renderStarmap}>
          {!configLoaded && !hasLocation ? (
            <LoadingContainer>
              <LoadingContent>LOADING ...</LoadingContent>
            </LoadingContainer>
          ) : (
            <>
              <DesignPanel loading={!configLoaded && !hasLocation} customerId={customerId} orderId={orderId} item={item} />
              <WideContainer>
                <PreviewPanelPrint />
              </WideContainer>
            </>
          )}
        </InnerContainer>
      </Container>
    );
  };

  return (
    <Layout
      pathname="/design"
      backgroundColor={!isNightSkyMaps ? tnsBlue : nsmWhite}
      footerBackgroundColor={!isNightSkyMaps ? tnsBlue : nsmWhite}
      footerTextColor={!isNightSkyMaps ? white : tnsBlue}
      headerBackgroundColor={!isNightSkyMaps ? tnsBlue : nsmWhite}
      headerText=""
      headerTextColor={!isNightSkyMaps ? white : tnsBlue}
      showHeader={false}
    >
      {render()}
    </Layout>
  );
};

Design.getInitialProps = async (ctx) => {
  const { orderId, itemId } = ctx.query;

  if (orderId && orderId !== "" && itemId && itemId !== "") {
    const order = await orderApi.getOrderForEdit(orderId);
    // console.log(`GOT ORDER FOR EDIT: `, order);
    if (order && order.cart && order.cart.items && order.cart.items.length > 0) {
      const { cart, email, customerId } = order;
      const matchingItems = cart.items.filter((i) => i.id === itemId);
      if (matchingItems && matchingItems.length > 0) {
        return {
          namespacesRequired: ["design"],
          pageProps: {
            item: matchingItems[0],
            orderId,
            customerId,
            email,
          },
        };
      }
    }
  }

  return {
    namespacesRequired: ["design"],
    pageProps: {},
  };
};

Design.defaultProps = {
  windowWidth: 1000,
  windowHeight: 1000,
  isMobile: false,
  pageProps: null,
};

Design.propTypes = {
  windowWidth: PropTypes.number,
  windowHeight: PropTypes.number,
  isMobile: PropTypes.bool,
  pageProps: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

const orrerySizesToProps = ({ width, height }) => {
  return {
    isMobile: width < 960, // || height < 800,
    windowWidth: width || 1000,
    windowHeight: height || 1000,
  };
};

export default withSizes(orrerySizesToProps)(Design);
